import type { ValidateFunction } from "ajv";
import { validateServerMessage, validateEntry, validateSettings, validateBindings, validateListing, validateFacts, validateProjectedValue } from "../generated/threadLive/validators.mjs";
import type { ProjectedValue, ServerToBrowserMessage, ThreadBindings, ThreadEntry, ThreadFacts, ThreadListing, ThreadSettings } from "../generated/threadLive/serverToBrowser";
import { JsonContractError } from "../http/jsonDecoding";

/**
 * The browser live messages (threads.md T-15, T-17), typed from `schema/thread-live/`
 * (`npm run live:generate`) and checked against the same schema at run time, so no message
 * shape is written by hand. Entries read over HTTP are the same model and are checked by
 * the same schema (`server/src/dmw/api/routes/threads/bodies.py`).
 */
export type {
  AgentRoute,
  AnnotationSet,
  ApiKeyRoute,
  Author,
  ActionRefusal,
  ChangeSetResult,
  ConditionOutcome,
  EntriesMessage,
  EntryMention,
  EntryPayload,
  ForkOfferedPayload,
  ForkOriginPayload,
  HostChangedPayload,
  HostingChangedPayload,
  HumanInputPayload,
  HumanStepRequestedPayload,
  InstanceDataSet,
  LiveErrorMessage,
  LocalToolCall,
  Mention,
  MessageDeltaEvent,
  MessagePayload,
  OpenRun,
  OutcomeNoticePayload,
  PendingToolApproval,
  PlanEntry,
  PlanEvent,
  ProblemPayload,
  ProjectedGroup,
  ProjectedHumanStep,
  RefusalConcern,
  RequestAudience,
  ProjectedStep,
  ProjectedTurn,
  ProjectedValue,
  ProjectedWait,
  ProjectedWorkflow,
  ResyncRequired,
  ServerToBrowserMessage,
  SettingsChangedPayload,
  StepAttempt,
  StepDetail,
  StepDisplayState,
  StepOutputPayload,
  StructuredOutput,
  ThreadBindings,
  ThreadDetail,
  ThreadDisplayStatus,
  ThreadEntry,
  ThreadFacts,
  ThreadListedMessage,
  ThreadListing,
  ThreadListMessage,
  ThreadProjection,
  ThreadSettings,
  ThreadStartedPayload,
  ThreadStateMessage,
  ThreadViewer,
  ToolCallEvent,
  ToolCallPayload,
  ToolCallSnapshot,
  ToolRunPlan,
  ToolRuntimeUsed,
  ToolTurnFinishedPayload,
  ToolTurnRequestedPayload,
  ToolValue,
  ToolValueOutput,
  TurnFinishedPayload,
  TurnProgressMessage,
  TurnRequestedPayload,
  TurnSnapshotMessage,
  TurnUsage,
  UsageTotals,
  ViewerAction,
  ViewerActionName,
  VersionsUsed,
  WaitingTool,
  WaitKind,
  WorkflowEventPayload,
} from "../generated/threadLive/serverToBrowser";
export type { BrowserToServerMessage } from "../generated/threadLive/browserToServer";

/** The kind of an entry's payload: `message`, `turn_requested`, … */
export type EntryKind = ThreadEntry["payload"]["kind"];

function violation(validate: ValidateFunction, context: string): JsonContractError {
  const first = validate.errors?.[0];
  const where = first === undefined || first.instancePath === "" ? "/" : first.instancePath;
  return new JsonContractError(`${context} breaks the live schema at ${where}: ${first?.message ?? "invalid"}.`);
}

/** Parse one text frame of the live socket. Throws `JsonContractError` for anything else. */
export function parseServerMessage(text: string): ServerToBrowserMessage {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new JsonContractError("A live message is not JSON.");
  }
  if (!validateServerMessage(value)) {
    throw violation(validateServerMessage, "A live message");
  }
  // Checked against the schema the type is generated from.
  return value as ServerToBrowserMessage;
}

/** One Thread entry, as `GET /threads/{id}/entries` returns it. */
export function readThreadEntry(value: unknown): ThreadEntry {
  if (!validateEntry(value)) {
    throw violation(validateEntry, "A Thread entry");
  }
  // Checked against the schema the type is generated from.
  return value as ThreadEntry;
}

/** A Thread's settings in an HTTP response: module C's own model, as in the live schema. */
export function readThreadSettings(value: unknown): ThreadSettings {
  if (!validateSettings(value)) {
    throw violation(validateSettings, "Thread settings");
  }
  // Checked against the schema the type is generated from.
  return value as ThreadSettings;
}

export function readThreadBindings(value: unknown): ThreadBindings {
  if (!validateBindings(value)) {
    throw violation(validateBindings, "Thread bindings");
  }
  // Checked against the schema the type is generated from.
  return value as ThreadBindings;
}

/** What a Thread's overview says of it now (W-22): module C's own model, as the live state carries it. */
export function readThreadFacts(value: unknown): ThreadFacts {
  if (!validateFacts(value)) {
    throw violation(validateFacts, "A Thread's facts");
  }
  // Checked against the schema the type is generated from.
  return value as ThreadFacts;
}

/** One line of the Thread list, as `GET /projects/{p}/threads` and the live socket send it. */
export function readThreadListing(value: unknown): ThreadListing {
  if (!validateListing(value)) {
    throw violation(validateListing, "A Thread list line");
  }
  // Checked against the schema the type is generated from.
  return value as ThreadListing;
}

/** A value at a glance, module C's summary (WD-4), as a plan's wired input and a switch's output carry it over HTTP. */
export function readProjectedValue(value: unknown): ProjectedValue {
  if (!validateProjectedValue(value)) {
    throw violation(validateProjectedValue, "A value at a glance");
  }
  // Checked against the schema the type is generated from.
  return value as ProjectedValue;
}
